import axios from 'axios';

// Configurar axios con la URL base del servidor
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

// Crear instancia de axios con configuración por defecto
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Interceptor para agregar el token JWT a todas las peticiones
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('saver-auth-token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para manejar errores de autenticación
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      // Token inválido o expirado - redirigir al login solo si no estamos ya ahí
      if (!window.location.pathname.includes('/login')) {
        localStorage.removeItem('saver-auth-token');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// ==================== TRANSACCIONES ====================

export const transactionsApi = {
  // Obtener todas las transacciones
  getAll: async () => {
    try {
      const response = await api.get('/api/transactions');
      return response.data.transactions || [];
    } catch (error) {
      console.error('Error obteniendo transacciones:', error);
      throw error;
    }
  },

  // Crear una transacción
  create: async (transaction) => {
    try {
      const response = await api.post('/api/transactions', { transaction });
      return response.data;
    } catch (error) {
      console.error('Error creando transacción:', error);
      throw error;
    }
  },

  // Crear múltiples transacciones
  createMany: async (transactions) => {
    try {
      const response = await api.post('/api/transactions', { transactions });
      return response.data;
    } catch (error) {
      console.error('Error creando transacciones:', error);
      throw error;
    }
  },

  // Eliminar una transacción
  delete: async (id) => {
    try {
      const response = await api.delete(`/api/transactions/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error eliminando transacción:', error);
      throw error;
    }
  }
};

// ==================== TASAS DE CONVERSIÓN ====================

export const conversionRatesApi = {
  // Obtener tasas de conversión
  get: async () => {
    try {
      const response = await api.get('/api/conversion-rates');
      return response.data.rates || {};
    } catch (error) {
      console.error('Error obteniendo tasas de conversión:', error);
      throw error;
    }
  },

  // Actualizar tasas de conversión
  update: async (rates) => {
    try {
      const response = await api.put('/api/conversion-rates', { rates });
      return response.data;
    } catch (error) {
      console.error('Error actualizando tasas de conversión:', error);
      throw error;
    }
  }
};

// ==================== CIERRE MENSUAL ====================

export const monthClosureApi = {
  // Obtener datos de cierre mensual
  get: async () => {
    try {
      const response = await api.get('/api/month-closure');
      return response.data.closure || null;
    } catch (error) {
      console.error('Error obteniendo cierre mensual:', error);
      throw error;
    }
  },

  // Actualizar datos de cierre mensual
  update: async (closure) => {
    try {
      const response = await api.put('/api/month-closure', { closure });
      return response.data;
    } catch (error) {
      console.error('Error actualizando cierre mensual:', error);
      throw error;
    }
  }
};

// ==================== MIGRACIÓN ====================

export const migrationApi = {
  // Migrar datos desde localStorage al servidor
  migrateFromLocalStorage: async () => {
    try {
      // Leer datos del localStorage
      const transactions = localStorage.getItem('saver-transactions');
      const conversionRates = localStorage.getItem('saver-conversion-rates');
      const monthClosure = localStorage.getItem('saver-month-closure');

      const data = {
        transactions: transactions ? JSON.parse(transactions) : null,
        conversionRates: conversionRates ? JSON.parse(conversionRates) : null,
        monthClosure: monthClosure ? JSON.parse(monthClosure) : null
      };

      const response = await api.post('/api/migrate-from-localstorage', data);
      return response.data;
    } catch (error) {
      console.error('Error migrando datos:', error);
      throw error;
    }
  }
};

// ==================== GOOGLE SHEETS ====================

export const googleSheetsApi = {
  // Importar desde Google Sheets - Un mes específico
  importMonth: async (year, month) => {
    try {
      const response = await api.post('/api/import-from-google-sheets', {
        year,
        month,
        importFullYear: false
      });
      return response.data;
    } catch (error) {
      console.error('Error importando mes desde Google Sheets:', error);
      throw error;
    }
  },

  // Importar desde Google Sheets - Año completo
  importYear: async (year) => {
    try {
      const response = await api.post('/api/import-from-google-sheets', {
        year,
        importFullYear: true
      });
      return response.data;
    } catch (error) {
      console.error('Error importando año desde Google Sheets:', error);
      throw error;
    }
  },

  // Sincronizar hacia Google Sheets
  syncToSheets: async (transactions) => {
    try {
      const response = await api.post('/api/sync-google-sheets', {
        transactions
      });
      return response.data;
    } catch (error) {
      console.error('Error sincronizando con Google Sheets:', error);
      throw error;
    }
  }
};

// ==================== PREFERENCIAS DE USUARIO ====================

export const preferencesApi = {
  get: async (key) => {
    try {
      const response = await api.get(`/api/preferences/${key}`);
      return response.data.value;
    } catch (error) {
      return null;
    }
  },
  set: async (key, value) => {
    try {
      await api.put(`/api/preferences/${key}`, { value });
    } catch (error) {
      console.error('Error guardando preferencia:', error);
    }
  }
};

// ==================== CONFIGURACIÓN DE PRESUPUESTO ====================

export const budgetSettingsApi = {
  get: async () => {
    try {
      const response = await api.get('/api/budget-settings');
      return response.data.settings || null;
    } catch (error) {
      console.error('Error obteniendo configuración de presupuesto:', error);
      return null;
    }
  },

  update: async (budgets, savings) => {
    try {
      const response = await api.put('/api/budget-settings', { budgets, savings });
      return response.data;
    } catch (error) {
      console.error('Error actualizando configuración de presupuesto:', error);
      throw error;
    }
  }
};

// ==================== IMPORTACIÓN DESDE EXCEL ====================

export const excelImportApi = {
  // Importar desde archivo Excel
  importFromFile: async (file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post('/api/import-from-excel', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      return response.data;
    } catch (error) {
      console.error('Error importando desde Excel:', error);
      throw error;
    }
  }
};

export default api;
